import React, { useState, useRef, useEffect } from "react";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { useChat } from "@/context/ChatContext";
import { useStorageUsage } from "@/hooks/useStorageUsage";
import DropZone from "./DropZone";
import MultipleFilePreview, { FileProgress } from "./MultipleFilePreview";
import CompactUploadButton from "./CompactUploadButton";
import { processMultipleFilesAndCreateMessage } from "@/context/chat/multipleFileProcessor";
import { DocumentAnalysis } from "@/context/chat/documentAnalysisService";
import { generateAIResponse } from "@/context/chat/aiResponseService";
import { initializePdfJs, isPdfWorkerAvailable } from "@/utils/pdfJsConfig";
import { Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface MultipleFileUploaderProps {
  onFilesSelected: (files: File[]) => void;
  selectedFiles: File[];
  onRemoveFile: (index: number) => void;
  disabled?: boolean;
  onClose: () => void;
  hasUploadedFiles?: boolean;
}

const MultipleFileUploader: React.FC<MultipleFileUploaderProps> = ({
  onFilesSelected,
  selectedFiles,
  onRemoveFile,
  disabled = false,
  onClose,
  hasUploadedFiles = false
}) => {
  const [filesProgress, setFilesProgress] = useState<FileProgress[]>([]);
  const [pdfWorkerLoaded, setPdfWorkerLoaded] = useState(false);
  const { isStorageLimitReached } = useStorageUsage();

  useEffect(() => {
    const initializePdf = async () => {
      try {
        // **CENTRALIZAÇÃO**: Usa apenas a Promise única centralizada
        const result = await initializePdfJs();
        setPdfWorkerLoaded(result);
      } catch (error) {
        console.warn("PDF.js initialization warning:", error);
        setPdfWorkerLoaded(false);
      }
    };
    
    initializePdf();
  }, []);

  const handleFilesSelected = (files: File[]) => {
    // Check storage limit before accepting files
    if (isStorageLimitReached()) {
      toast({
        title: "Armazenamento Cheio",
        description: "Você atingiu o limite de armazenamento. Exclua arquivos antigos ou faça upgrade do plano.",
        variant: "destructive",
      });
      return;
    }

    onFilesSelected(files);
    
    // Initialize progress tracking
    const initialProgress: FileProgress[] = files.map(file => ({
      file,
      progress: 0,
      status: 'pending' as const
    }));
    
    setFilesProgress(initialProgress);
    
    toast({
      title: "Arquivos Adicionados",
      description: `${files.length} arquivo(s) prontos para envio. Digite uma mensagem e envie.`,
      variant: "default",
    });
  };

  const handleProgressUpdate = (updatedProgress: FileProgress[]) => {
    setFilesProgress(updatedProgress);
  };

  const handleRemoveFile = (index: number) => {
    onRemoveFile(index);
    
    const newProgress = filesProgress.filter((_, i) => i !== index);
    setFilesProgress(newProgress);
    
    if (selectedFiles.length - 1 === 0) {
      toast({
        title: "Arquivos Removidos",
        description: "Todos os arquivos foram removidos.",
        variant: "default",
      });
    }
  };

  // Atualizar progress quando os arquivos selecionados mudarem
  useEffect(() => {
    const initialProgress: FileProgress[] = selectedFiles.map(file => ({
      file,
      progress: 0,
      status: 'pending' as const
    }));
    
    setFilesProgress(initialProgress);
  }, [selectedFiles]);

  return (
    <div className="space-y-4">
      {/* Header - REMOVER quando modo compacto */}
      {!hasUploadedFiles && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold">Upload Múltiplo</h3>
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button 
                    type="button"
                    className="inline-flex items-center justify-center rounded-full w-5 h-5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    onClick={(e) => {
                      e.currentTarget.dataset.state = e.currentTarget.dataset.state === 'open' ? 'closed' : 'open';
                    }}
                  >
                    <Info className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs z-[9999]">
                  <div className="space-y-1 text-xs">
                    <p className="font-semibold mb-2">🚀 Recursos ao enviar:</p>
                    <p>• Processamento simultâneo de múltiplos PDFs</p>
                    <p>• Análise cruzada e detecção de relacionamentos</p>
                    <p>• IA especializada em direito brasileiro (laib-3.7)</p>
                    <p>• Consolidação inteligente de informações</p>
                    <p>• Preenchimento automático de contratos</p>
                    <p>• Detecção de inconsistências entre documentos</p>
                    <p>• Geração de relatórios executivos consolidados</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          {selectedFiles.length > 0 && (
            <span className="text-sm text-muted-foreground">
              {selectedFiles.length} arquivo(s) pronto(s) para envio
            </span>
          )}
        </div>
      )}

      {/* File Selection - DropZone OU Botão Compacto */}
      {selectedFiles.length === 0 ? (
        hasUploadedFiles ? (
          // MODO COMPACTO: Botão pequeno
          <CompactUploadButton
            onFilesSelected={handleFilesSelected}
            disabled={disabled}
          />
        ) : (
          // DropZone completo (primeira vez)
          <DropZone
            onFileSelected={handleFilesSelected}
            disabled={disabled}
            selectedFiles={selectedFiles}
          />
        )
      ) : (
        <MultipleFilePreview
          filesProgress={filesProgress}
          onRemoveFile={handleRemoveFile}
          disabled={disabled}
          isProcessing={false}
        />
      )}

      {/* Instructions - REMOVER quando modo compacto */}
      {selectedFiles.length > 0 && !hasUploadedFiles && (
        <div className="text-sm p-3 bg-primary/10 rounded-md border border-primary/20">
          <div className="font-medium text-primary mb-1">
            Arquivos prontos para envio
          </div>
          <p className="text-primary/80">
            Digite uma mensagem e clique em enviar para processar os {selectedFiles.length} arquivo(s) com análise avançada.
          </p>
        </div>
      )}
    </div>
  );
};

export default MultipleFileUploader;